/**
 * Locale-aware rendering for unit abbreviations.
 *
 * `units.abbreviation` is the canonical English token (`pc`, `pinch`, `tsp`,
 * `tbsp`, `cup`, `fl oz`, `slice`, `clove`, `bunch`). The DB is correctly
 * single-locale — keeping unit semantics out of i18n message catalogues.
 * This util maps each canonical token to its locale-appropriate display
 * string at render time.
 *
 * Internationally-legible tokens (`g`, `kg`, `mg`, `ml`, `l`, `oz`, `lb`)
 * pass through unchanged in every locale. The German overrides cover the
 * count + recipe-specific volume vocabulary that an English abbreviation
 * looks foreign in (`tsp` → `TL`, `tbsp` → `EL`).
 *
 * `pc` is the generic counter and is dropped in every locale: "1 piece
 * egg" / "1 Stück Ei" read worse than "1 egg" / "1 Ei". Specific count
 * units (`clove`, `bunch`, `slice`, `head`, `stalk`, `sprig`, `leaf`,
 * `strip`, `fillet`) ARE informative — "1 clove garlic" beats "1 garlic"
 * — so they keep their localised label.
 *
 * Pluralisation is not needed: the listed German forms (`Prise`, `TL`,
 * `EL`, `Bund`, `Scheibe`, `Zehe`) are invariant in the contexts they
 * appear (ingredient lines and step text).
 */

import type { Locale } from 'next-intl';

const DE_OVERRIDES: Record<string, string> = {
  pinch: 'Prise',
  clove: 'Zehe',
  bunch: 'Bund',
  slice: 'Scheibe',
  tsp: 'TL',
  tbsp: 'EL',
  cup: 'Tasse',
  // Less common count units that AI imports also emit (e.g. shoppingUnit for
  // garlic = "head"). Without these the German UI leaks the English word.
  head: 'Knolle',
  stalk: 'Stange',
  sprig: 'Zweig',
  leaf: 'Blatt',
  strip: 'Streifen',
  fillet: 'Filet',
};

export function unitLabel(abbrev: string | null | undefined, locale: Locale | string): string {
  if (!abbrev) return '';
  if (abbrev === 'pc') return '';
  if (locale === 'de') {
    return DE_OVERRIDES[abbrev] ?? abbrev;
  }
  return abbrev;
}
